import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import { API_CONFIG } from '../../../config/api';
import { buildDeadlinePresets } from './buildDeadlinePresets';
import './DeadlineCheckerPage.css';

export default function DeadlineCheckerPage() {
  const navigate = useNavigate();
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [filterExam, setFilterExam] = useState('ALL');
  const [loadError, setLoadError] = useState(false);
  const [verifiedOn, setVerifiedOn] = useState('');
  const [cycle, setCycle] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      try {
        const response = await fetch(`${API_CONFIG.baseURL}/api/admissions/dates`, {
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Admissions dates unavailable');
        const register = await response.json();
        const loaded = buildDeadlinePresets(register);
        if (!controller.signal.aborted) {
          setPresets(loaded);
          setSelectedPreset(loaded[0] || null);
          setVerifiedOn(register.last_updated);
          setCycle(register.cycle);
        }
      } catch (error) {
        if (!controller.signal.aborted) setLoadError(true);
      }
    }
    load();
    return () => controller.abort();
  }, []);

  const filteredPresets = filterExam === 'ALL'
    ? presets
    : presets.filter(p => p.exam === filterExam);

  if (!selectedPreset) {
    return (
      <div className="deadline-tool-wrapper">
        <Header />
        <main className="deadline-tool-container">
          <h1 className="tool-title">Admissions Test & Deadline Checker</h1>
          <p role="status">
            {loadError ? 'Admissions dates are temporarily unavailable. Please try again later.' : 'Loading admissions dates…'}
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="deadline-tool-wrapper">
      <Header />
      
      <main className="deadline-tool-container">
        <div className="tool-hero-badge">UK Admissions Dates {cycle}</div>
        <h1 className="tool-title">Admissions Test & Deadline Checker</h1>
        <p className="tool-subtitle">
          Select your target university and course to view verified registration deadlines, 
          sitting windows, Pearson VUE fees, and test format specifications.
        </p>

        {/* Filter Pills */}
        <div className="filter-pill-bar">
          {['ALL', 'TARA', 'ESAT', 'LNAT', 'UCAT'].map(exam => (
            <button
              key={exam}
              className={`filter-pill ${filterExam === exam ? 'active' : ''}`}
              onClick={() => {
                setFilterExam(exam);
                const firstMatch = exam === 'ALL' ? presets[0] : presets.find(p => p.exam === exam);
                if (firstMatch) setSelectedPreset(firstMatch);
              }}
            >
              {exam === 'ALL' ? 'All Exams' : exam}
            </button>
          ))}
        </div>

        <div className="deadline-grid-layout">
          {/* Selector List */}
          <div className="presets-list-pane">
            <h3 className="pane-section-title">Select Target University Course</h3>
            <div className="preset-cards-stack">
              {filteredPresets.map((preset, idx) => (
                <div
                  key={idx}
                  className={`preset-card ${selectedPreset.course === preset.course && selectedPreset.university === preset.university ? 'selected' : ''}`}
                  onClick={() => setSelectedPreset(preset)}
                >
                  <div className="preset-card-header">
                    <span className="preset-uni">{preset.university}</span>
                    <span className={`exam-badge badge-${preset.exam.toLowerCase()}`}>{preset.exam}</span>
                  </div>
                  <div className="preset-course">{preset.course}</div>
                  <div className="preset-deadline-preview">Deadline: {preset.deadline}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Details & Action Pane */}
          <div className="details-card-pane">
            <div className="details-card">
              <div className="details-header">
                <div>
                  <span className="exam-tag">{selectedPreset.examTitle}</span>
                  <h2 className="selected-uni-title">{selectedPreset.university}</h2>
                  <p className="selected-course-title">{selectedPreset.course}</p>
                </div>
              </div>

              <div className="specs-metrics-grid">
                <div className="metric-box">
                  <span className="metric-label">Registration / Booking Deadline</span>
                  <span className="metric-value text-red">{selectedPreset.deadline}</span>
                </div>
                <div className="metric-box">
                  <span className="metric-label">Official Sitting Window</span>
                  <span className="metric-value text-blue">{selectedPreset.sitWindow}</span>
                </div>
                <div className="metric-box">
                  <span className="metric-label">Consortium Test Fee</span>
                  <span className="metric-value">{selectedPreset.fee}</span>
                </div>
                <div className="metric-box">
                  <span className="metric-label">Entry Year</span>
                  <span className="metric-value text-green">{selectedPreset.status}</span>
                </div>
              </div>

              <div className="verified-provenance-box">
                <span className="provenance-dot"></span>
                <span>Checked against official UCAT, UAT-UK and LNAT sources • {verifiedOn}</span>
              </div>

              <div className="notes-block">
                <strong>Important Candidate Guidance:</strong>
                <p>{selectedPreset.notes}</p>
              </div>

              <div className="action-buttons-row">
                <button
                  className="btn-start-prep"
                  onClick={() => navigate('/practice')}
                >
                  Start {selectedPreset.exam} Practice Simulation
                </button>
                <button
                  className="btn-learn-more"
                  onClick={() => navigate(selectedPreset.link)}
                >
                  View Full {selectedPreset.exam} Guide
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
